import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency, formatDate, monthNames, getLocalDateString } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';
import { HelpTip } from '@/components/HelpTip';

const Transactions = () => {
  const [searchParams] = useSearchParams();
  const urlType = searchParams.get('type');
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [transactionType, setTransactionType] = useState(urlType || 'income');
  const [allTransactions, setAllTransactions] = useState([]);
  const [formData, setFormData] = useState({
    type: urlType || 'income',
    amount: '',
    category_id: '',
    date: getLocalDateString(),
    description: '',
  });

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: transactionType };
      if (filterMonth) params.month = filterMonth;
      if (filterYear) params.year = filterYear;
      if (filterCategoryId) params.category_id = filterCategoryId;
      const response = await api.get('/transactions', { params });
      setTransactions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  }, [transactionType, filterMonth, filterYear, filterCategoryId]);

  const loadAllForSummary = useCallback(async () => {
    try {
      const params = {};
      if (filterMonth) params.month = filterMonth;
      if (filterYear) params.year = filterYear;
      if (filterCategoryId) params.category_id = filterCategoryId;
      const response = await api.get('/transactions', { params });
      setAllTransactions(response.data);
    } catch {
      // silent
    }
  }, [filterMonth, filterYear, filterCategoryId]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  }, []);

  useEffect(() => {
    loadTransactions();
    loadAllForSummary();
    loadCategories();
  }, [loadTransactions, loadAllForSummary, loadCategories]);

  useEffect(() => {
    if (urlType) {
      setTransactionType(urlType);
      setFormData(prev => ({ ...prev, type: urlType }));
    }
  }, [urlType]);

  const summary = useMemo(() => {
    const income = allTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + (t.amount || 0), 0);
    const expense = allTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + (t.amount || 0), 0);
    return { income: Math.round(income * 100) / 100, expense: Math.round(expense * 100) / 100 };
  }, [allTransactions]);

  const filterCategories = useMemo(() => {
    const targetType = transactionType === 'income' ? 'entrada' : 'saida';
    return categories.filter(c => c.type === targetType);
  }, [categories, transactionType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTransaction) {
        await api.put(`/transactions/${editingTransaction.id}`, formData);
        toast.success('Transação atualizada!');
      } else {
        await api.post('/transactions', formData);
        toast.success('Transação criada!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadTransactions();
      loadAllForSummary();
    } catch (error) {
      toast.error('Erro ao salvar transação');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir esta transação?')) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success('Transação excluída!');
      loadTransactions();
      loadAllForSummary();
    } catch (error) {
      toast.error('Erro ao excluir transação');
    }
  };

  const resetForm = () => {
    setFormData({
      type: transactionType,
      amount: '',
      category_id: '',
      date: getLocalDateString(),
      description: '',
    });
    setEditingTransaction(null);
  };

  const openEditDialog = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      type: transaction.type,
      amount: transaction.amount.toString(),
      category_id: transaction.category_id,
      date: transaction.date,
      description: transaction.description || '',
    });
    setIsDialogOpen(true);
  };

  const getCategoryName = (categoryId) => {
    return categories.find((c) => c.id === categoryId)?.name || 'Sem categoria';
  };

  const filteredFormCategories = useMemo(() => {
    const targetType = formData.type === 'income' ? 'entrada' : 'saida';
    return categories.filter((cat) => cat.type === targetType);
  }, [categories, formData.type]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div data-testid="transactions-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {transactionType === 'income' ? 'Entradas' : 'Saídas'}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Gerencie suas transações financeiras
          <HelpTip text={transactionType === 'income' ? 'Registre aqui todas as vendas e receitas do seu negócio.' : 'Registre aqui todos os gastos e despesas do seu negócio.'} className="ml-1" />
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
        <Card data-testid="summary-income">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Entradas</CardTitle>
            <TrendingUp className="text-emerald-600" size={18} />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatCurrency(summary.income)}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-expense">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Saídas</CardTitle>
            <TrendingDown className="text-rose-600" size={18} />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-rose-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatCurrency(summary.expense)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
        <div className="flex gap-2">
          <Button
            variant={transactionType === 'income' ? 'default' : 'outline'}
            onClick={() => { setTransactionType('income'); setFilterCategoryId(''); }}
            data-testid="filter-income"
            size="sm"
            className={transactionType === 'income' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <TrendingUp size={16} className="mr-1.5" />
            Entradas
          </Button>
          <Button
            variant={transactionType === 'expense' ? 'default' : 'outline'}
            onClick={() => { setTransactionType('expense'); setFilterCategoryId(''); }}
            data-testid="filter-expense"
            size="sm"
            className={transactionType === 'expense' ? 'bg-rose-600 hover:bg-rose-700' : ''}
          >
            <TrendingDown size={16} className="mr-1.5" />
            Saídas
          </Button>
        </div>

        <Select value={filterMonth || 'all'} onValueChange={(val) => setFilterMonth(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-[150px]" data-testid="filter-month">
            <SelectValue placeholder="Todos os meses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os meses</SelectItem>
            {monthNames.map((month, idx) => (
              <SelectItem key={month} value={(idx + 1).toString()}>{month}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterYear || 'all'} onValueChange={(val) => setFilterYear(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-[120px]" data-testid="filter-year">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterCategoryId || 'all'} onValueChange={(val) => setFilterCategoryId(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category">
            <SelectValue placeholder="Todas categorias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            {filterCategories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-transaction-button" className="bg-indigo-600 hover:bg-indigo-700 ml-auto" size="sm">
              <Plus size={16} className="mr-1.5" />
              Nova Transação
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="transaction-dialog">
            <DialogHeader>
              <DialogTitle>{editingTransaction ? 'Editar' : 'Nova'} Transação</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Tipo</Label>
                <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val, category_id: '' })}>
                  <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Entrada</SelectItem>
                    <SelectItem value="expense">Saída</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor (R$)</Label>
                <Input type="number" step="0.01" data-testid="transaction-amount-input" value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={formData.category_id} onValueChange={(val) => setFormData({ ...formData, category_id: val })}>
                  <SelectTrigger data-testid="transaction-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {filteredFormCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data</Label>
                <Input type="date" data-testid="transaction-date-input" value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
              </div>
              <div>
                <Label>Descrição (Opcional)</Label>
                <Textarea data-testid="transaction-description-input" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>
              <Button type="submit" data-testid="transaction-submit-button" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {editingTransaction ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <Card data-testid="transactions-list">
        <CardHeader>
          <CardTitle>Transações</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhuma transação encontrada</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Categoria</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground hidden sm:table-cell">Descrição</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Valor</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="border-b border-border hover:bg-accent transition-colors"
                      data-testid={`transaction-row-${transaction.id}`}>
                      <td className="py-3 px-4 text-sm text-foreground">{formatDate(transaction.date)}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{getCategoryName(transaction.category_id)}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground hidden sm:table-cell">{transaction.description || '-'}</td>
                      <td className="py-3 px-4 text-sm text-right font-semibold">
                        <span className={transaction.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}>
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEditDialog(transaction)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded"
                            data-testid={`edit-transaction-${transaction.id}`}>
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => handleDelete(transaction.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"
                            data-testid={`delete-transaction-${transaction.id}`}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Transactions;
